import type { ListingTemplateProduct } from "@/lib/listing-templates";
import { Stars, TemplatePoweredFooter } from "./shared";
import { pickImage, brandOf, trustSections } from "./template-helpers";

/**
 * Template 2 — Premium Brand.
 * Sophisticated presentation with a dark branded header, refined typography,
 * lifestyle imagery, elegant feature highlights and trust sections.
 */
export default function PremiumBrandTemplate({ product }: { product: ListingTemplateProduct }) {
  const brand = brandOf(product);
  const heroImage = pickImage(product, [2, 1, 0]);
  const detailLeft = pickImage(product, [1, 0]);
  const detailRight = pickImage(product, [4, 1, 0]);
  const sections = trustSections(product);

  const trustItems = [
    brand ? `Handcrafted by ${brand}` : "Thoughtfully made",
    product.warranty ?? "Quality guaranteed",
    "Fast, insured shipping",
    product.rating.count > 0
      ? `${product.rating.score.toFixed(1)}★ from ${product.rating.count} reviews`
      : "Top-rated",
  ];

  return (
    <div className="bg-stone-50 text-stone-900" style={{ fontFamily: "'Outfit', sans-serif" }}>
      {/* Branded hero header */}
      <header className="bg-stone-950 text-stone-100">
        <div className="max-w-6xl mx-auto px-6 pt-6 pb-16">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full border border-amber-400/50 flex items-center justify-center text-amber-300">
                <i className="ri-vip-crown-line" />
              </span>
              {brand && (
                <p className="text-sm font-semibold tracking-[0.2em] uppercase text-amber-300">{brand}</p>
              )}
            </div>
            {/* Navigation is omitted until merchant-configurable brand settings exist. */}
          </div>

          <div className="mt-12 max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-light leading-tight tracking-tight break-words">
              {product.title}
            </h1>
            <p className="mt-5 text-stone-300 leading-relaxed max-w-2xl">{product.shortDescription}</p>
            <div className="mt-6 flex items-center gap-4">
              <span className="text-2xl font-light text-amber-200">{product.price}</span>
              {product.wasPrice && <span className="text-stone-500 line-through">{product.wasPrice}</span>}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 -mt-6">
        {/* Primary product image */}
        {heroImage && (
          <div className="rounded-2xl overflow-hidden bg-stone-100 shadow-xl shadow-stone-900/10">
            <img
              src={heroImage.src}
              alt={heroImage.alt}
              data-testid="template-hero-image"
              className="w-full h-[360px] md:h-[480px] object-contain object-center"
            />
          </div>
        )}

        {/* Trust strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-200 rounded-xl overflow-hidden mt-8">
          {trustItems.map((item) => (
            <div key={item} className="bg-white px-5 py-5 text-center">
              <i className="ri-check-double-line text-amber-600 text-xl" />
              <p className="text-sm text-stone-600 mt-2 break-words">{item}</p>
            </div>
          ))}
        </div>

        {/* Feature highlights — numbered elegance */}
        {product.keyFeatures.length > 0 && (
          <section className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-amber-700 mb-3">Key features</p>
              <h2 className="text-2xl font-light tracking-tight mb-8">Crafted to perform</h2>
              <div className="space-y-6">
                {product.keyFeatures.slice(0, 4).map((feature, i) => (
                  <div key={feature} className="flex gap-5">
                    <span className="text-3xl font-light text-amber-300">0{i + 1}</span>
                    <p className="text-stone-700 leading-relaxed pt-1 break-words">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
            {product.images.length > 1 && (
              <div className="grid grid-cols-2 gap-4">
                {detailLeft && (
                  <img
                    src={detailLeft.src}
                    alt={detailLeft.alt}
                    className="rounded-xl object-cover object-top w-full aspect-square"
                  />
                )}
                {detailRight && (
                  <img
                    src={detailRight.src}
                    alt={detailRight.alt}
                    className="rounded-xl object-cover object-top w-full aspect-square mt-8"
                  />
                )}
              </div>
            )}
          </section>
        )}

        {/* Description */}
        {product.fullDescription.length > 0 && (
          <section className="mt-20 max-w-3xl mx-auto text-center">
            <p className="text-xs tracking-[0.25em] uppercase text-amber-700 mb-3">About this product</p>
            <h2 className="text-2xl font-light tracking-tight mb-6">Details</h2>
            <div className="space-y-5 text-stone-600 leading-relaxed">
              {product.fullDescription.map((para) => (
                <p key={para.slice(0, 24)}>{para}</p>
              ))}
            </div>
          </section>
        )}

        {/* Specification table */}
        {product.specifications.length > 0 && (
          <section className="mt-20">
            <h2 className="text-2xl font-light tracking-tight mb-6 text-center">Specifications</h2>
            <div className="max-w-2xl mx-auto border-t border-stone-200">
              {product.specifications.map((spec) => (
                <div
                  key={spec.label}
                  className="flex flex-col sm:flex-row sm:justify-between gap-1 py-3.5 border-b border-stone-200"
                >
                  <span className="text-stone-500">{spec.label}</span>
                  <span className="font-medium text-stone-800 sm:text-right break-words">{spec.value}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Rating + CTA */}
        <section className="mt-16 bg-stone-950 text-stone-100 rounded-2xl px-8 py-12 text-center">
          <Stars score={product.rating.score} count={product.rating.count} className="justify-center" />
          <p className="text-2xl font-light mt-4">
            Trusted by {product.rating.count}+ customers
          </p>
          <div className="mt-6 flex items-center justify-center gap-3">
            <button className="px-8 py-3.5 bg-amber-500 hover:bg-amber-400 text-stone-950 font-semibold rounded-full whitespace-nowrap">
              Add to cart
            </button>
            <button className="px-8 py-3.5 border border-stone-600 hover:border-stone-400 text-stone-200 font-medium rounded-full whitespace-nowrap">
              Enquire
            </button>
          </div>
        </section>

        {/* Support */}
        {sections.length > 0 && (
          <section className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {sections.map((section) => (
              <div key={section.title} className="text-center">
                <h3 className="font-semibold text-stone-800">{section.title}</h3>
                <ul className="mt-3 space-y-1.5 text-sm text-stone-500">
                  {section.lines.map((line) => (
                    <li key={line} className="break-words">
                      {line}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}
      </main>

      <footer className="mt-16 bg-stone-950 text-stone-400">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <TemplatePoweredFooter
            testId="premium-brand-footer"
            left={brand ?? "Store"}
            sku={product.sku}
            leftClassName="tracking-[0.2em] uppercase"
            centerClassName="text-center text-stone-500"
          />
        </div>
      </footer>
    </div>
  );
}